import React, { useState, useEffect } from 'react';
import { format, addMonths, startOfMonth, endOfMonth, isSameDay, isAfter, isBefore, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays, Check, X, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

export const AvailabilityCalendar = ({ listingId, onUpdate, hourlyRate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({});
  const [bookedDates, setBookedDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectionMode, setSelectionMode] = useState('available'); // 'available' or 'blocked'
  const [activePreset, setActivePreset] = useState(null);
  
  // Time-based availability
  const [timeDialog, setTimeDialog] = useState({ open: false, date: null });
  const [availabilityTimes, setAvailabilityTimes] = useState({ startTime: '08:00', endTime: '20:00' });

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchAvailability();
  }, [listingId, currentMonth]);

  const fetchAvailability = async () => {
    if (!listingId) return;
    
    setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(addMonths(currentMonth, 1)), 'yyyy-MM-dd');
      const headers = getAuthHeaders();
      
      const res = await axios.get(
        `${API}/availability/${listingId}/calendar?start_date=${start}&end_date=${end}`,
        { withCredentials: true, headers }
      );
      
      // Convert availability array to map
      const availMap = {};
      res.data.availability.forEach(a => {
        availMap[a.date] = a;
      });
      setAvailability(availMap);
      
      // Extract booked dates
      const booked = res.data.booked_slots.map(b => ({
        start: parseISO(b.start_time),
        end: parseISO(b.end_time)
      }));
      setBookedDates(booked);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const isDateBooked = (date) => {
    return bookedDates.some(b => 
      (isAfter(date, b.start) || isSameDay(date, b.start)) && 
      (isBefore(date, b.end) || isSameDay(date, b.end))
    );
  };

  const isDateAvailable = (dateStr) => {
    const avail = availability[dateStr];
    return avail?.available !== false; // Default to available if not set
  };

  const isDateSelected = (date) => {
    return selectedDates.some(d => isSameDay(d, date));
  };

  const handleDateClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Can't modify past dates
    if (isBefore(date, new Date()) && !isSameDay(date, new Date())) {
      return;
    }
    
    // Can't modify booked dates
    if (isDateBooked(date)) {
      toast.error('This date has an active booking');
      return;
    }

    if (isDateSelected(date)) {
      setSelectedDates(selectedDates.filter(d => !isSameDay(d, date)));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const handleSaveChanges = async () => {
    if (selectedDates.length === 0) {
      toast.error('No dates selected');
      return;
    }

    setSaving(true);
    try {
      const dates = selectedDates.map(d => ({
        date: format(d, 'yyyy-MM-dd'),
        available: selectionMode === 'available',
        start_time: availabilityTimes.startTime,
        end_time: availabilityTimes.endTime
      }));
      const headers = getAuthHeaders();

      await axios.post(`${API}/availability/bulk`, {
        listing_id: listingId,
        dates
      }, { withCredentials: true, headers });

      toast.success(`${selectedDates.length} dates ${selectionMode === 'available' ? 'opened' : 'blocked'} (${availabilityTimes.startTime} - ${availabilityTimes.endTime})`);
      setSelectedDates([]);
      fetchAvailability();
      onUpdate?.();
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to update availability');
    } finally {
      setSaving(false);
    }
  };

  // Time options for availability
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  // Smart presets — auto-select days + times
  const getDaysForPreset = (presetId) => {
    const today = new Date();
    const endDate = endOfMonth(addMonths(currentMonth, 1));
    const days = [];
    let d = new Date(Math.max(today.getTime(), startOfMonth(currentMonth).getTime()));
    
    while (d <= endDate) {
      const dayOfWeek = d.getDay(); // 0=Sun, 6=Sat
      const isPast = isBefore(d, today) && !isSameDay(d, today);
      if (!isPast && !isDateBooked(d)) {
        if (presetId === 'work' && dayOfWeek >= 1 && dayOfWeek <= 5) days.push(new Date(d));
        else if (presetId === 'school' && dayOfWeek >= 1 && dayOfWeek <= 5) days.push(new Date(d));
        else if (presetId === 'weekends' && (dayOfWeek === 0 || dayOfWeek === 6)) days.push(new Date(d));
        else if (presetId === 'evenings') days.push(new Date(d));
        else if (presetId === 'all') days.push(new Date(d));
      }
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const presets = [
    { id: 'work', label: 'While I\'m at work', subtitle: 'Mon–Fri, 8am–6pm', icon: '💼', start: '08:00', end: '18:00', hoursPerDay: 10, daysPerWeek: 5 },
    { id: 'school', label: 'School hours', subtitle: 'Mon–Fri, 9am–3pm', icon: '🎒', start: '09:00', end: '15:00', hoursPerDay: 6, daysPerWeek: 5 },
    { id: 'weekends', label: 'Weekends', subtitle: 'Sat & Sun, all day', icon: '☀️', start: '07:00', end: '22:00', hoursPerDay: 15, daysPerWeek: 2 },
    { id: 'evenings', label: 'Every evening', subtitle: 'Daily, 6pm–11pm', icon: '🌙', start: '18:00', end: '23:00', hoursPerDay: 5, daysPerWeek: 7 },
    { id: 'all', label: 'Whenever I\'m not home', subtitle: 'Every day, set your hours', icon: '🏠', start: '08:00', end: '20:00', hoursPerDay: 12, daysPerWeek: 7 },
  ];

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    setSelectionMode('available');
    setAvailabilityTimes({ startTime: preset.start, endTime: preset.end });
    const days = getDaysForPreset(preset.id);
    setSelectedDates(days);
  };

  const estimateEarnings = (preset) => {
    const rate = hourlyRate || 8;
    const weeklyHours = preset.hoursPerDay * preset.daysPerWeek;
    // Assume ~60% utilisation for realistic estimate
    const utilisation = 0.6;
    const weeklyEarnings = rate * weeklyHours * utilisation * (1 - 0.05); // After 5% fee
    return Math.round(weeklyEarnings);
  };

  const renderCalendar = (month) => {
    const monthStart = startOfMonth(month);
    const startDay = monthStart.getDay(); // 0 = Sunday
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(month.getFullYear(), month.getMonth(), day);
      const dateStr = format(date, 'yyyy-MM-dd');
      const isPast = isBefore(date, new Date()) && !isSameDay(date, new Date());
      const isBooked = isDateBooked(date);
      const isAvailable = isDateAvailable(dateStr);
      const isSelected = isDateSelected(date);
      const isToday = isSameDay(date, new Date());

      let className = 'h-10 w-full rounded-lg text-sm font-medium transition-all ';
      
      if (isPast) {
        className += 'text-slate-300 cursor-not-allowed';
      } else if (isBooked) {
        className += 'bg-blue-100 text-blue-700 cursor-not-allowed';
      } else if (isSelected) {
        className += selectionMode === 'available' 
          ? 'bg-[#5ebc67] text-white ring-2 ring-[#5ebc67] ring-offset-2' 
          : 'bg-red-500 text-white ring-2 ring-red-500 ring-offset-2';
      } else if (!isAvailable) {
        className += 'bg-slate-100 text-slate-400 hover:bg-slate-200 cursor-pointer';
      } else {
        className += 'bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer';
      }

      if (isToday) {
        className += ' ring-2 ring-slate-900';
      }

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(date)}
          disabled={isPast || isBooked}
          className={className}
          data-testid={`date-${dateStr}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#5ebc67]" />
          Availability Calendar
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Smart Schedule Presets */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#5ebc67]" />
            <span className="text-sm font-semibold text-slate-800">When is your driveway empty?</span>
          </div>
          <p className="text-xs text-slate-500">Pick a schedule that matches your routine — we'll select the right days and times for you</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {presets.map((preset) => {
              const weekly = estimateEarnings(preset);
              const isActive = activePreset === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                    isActive 
                      ? 'border-[#5ebc67] bg-green-50 ring-1 ring-[#5ebc67]' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`preset-${preset.id}`}
                >
                  <span className="text-xl shrink-0">{preset.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{preset.label}</p>
                    <p className="text-xs text-slate-500">{preset.subtitle}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-[#5ebc67]">${weekly}</p>
                    <p className="text-[10px] text-slate-400">/week est.</p>
                  </div>
                </button>
              );
            })}
          </div>
          {activePreset && selectedDates.length > 0 && (
            <div className="flex items-center gap-2 p-2.5 bg-green-50 rounded-lg border border-green-200">
              <Check className="w-4 h-4 text-green-600 shrink-0" />
              <p className="text-xs text-green-700">
                <strong>{selectedDates.length} days</strong> selected for the next 2 months ({availabilityTimes.startTime}–{availabilityTimes.endTime}). 
                Estimated <strong>${estimateEarnings(presets.find(p => p.id === activePreset))}/week</strong> at ${hourlyRate || 8}/hr. Scroll down to save.
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100 pt-4">
          <p className="text-xs text-slate-400 mb-3">Or manually select dates below</p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-50 border border-green-200" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-slate-100 border border-slate-200" />
            <span>Blocked</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200" />
            <span>Booked</span>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="flex gap-2">
          <Button
            variant={selectionMode === 'available' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectionMode('available')}
            className={selectionMode === 'available' ? 'bg-[#5ebc67] hover:bg-[#4da855]' : ''}
            data-testid="mode-available"
          >
            <Check className="w-4 h-4 mr-1" />
            Mark Available
          </Button>
          <Button
            variant={selectionMode === 'blocked' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectionMode('blocked')}
            className={selectionMode === 'blocked' ? 'bg-red-500 hover:bg-red-600' : ''}
            data-testid="mode-blocked"
          >
            <X className="w-4 h-4 mr-1" />
            Block Dates
          </Button>
        </div>

        {/* Time-based availability settings */}
        {selectionMode === 'available' && (
          <div className="p-4 bg-green-50 rounded-xl border border-green-100">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Availability Hours</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-green-700">Available From</Label>
                <Select value={availabilityTimes.startTime} onValueChange={(val) => setAvailabilityTimes(t => ({ ...t, startTime: val }))}>
                  <SelectTrigger className="bg-white" data-testid="availability-start-time">
                    <SelectValue placeholder="Start time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-green-700">Available Until</Label>
                <Select value={availabilityTimes.endTime} onValueChange={(val) => setAvailabilityTimes(t => ({ ...t, endTime: val }))}>
                  <SelectTrigger className="bg-white" data-testid="availability-end-time">
                    <SelectValue placeholder="End time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeOptions.map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2 mt-3">
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="text-xs"
                onClick={() => setAvailabilityTimes({ startTime: '08:00', endTime: '18:00' })}
              >
                Business Hours (8am-6pm)
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="text-xs"
                onClick={() => setAvailabilityTimes({ startTime: '00:00', endTime: '23:30' })}
              >
                24/7
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="text-xs"
                onClick={() => setAvailabilityTimes({ startTime: '17:00', endTime: '08:00' })}
              >
                Evenings Only
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="text-xs"
                onClick={() => setAvailabilityTimes({ startTime: '09:00', endTime: '17:00' })}
              >
                Weekday Daytime
              </Button>
            </div>
          </div>
        )}

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
            data-testid="prev-month"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h3 className="font-semibold text-lg">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            data-testid="next-month"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#5ebc67]" />
          </div>
        ) : (
          <div>
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-slate-500">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar(currentMonth)}
            </div>
          </div>
        )}

        {/* Selected dates summary */}
        {selectedDates.length > 0 && (
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-slate-500">
                  Will be {selectionMode === 'available' ? 'opened for bookings' : 'blocked'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedDates([])}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveChanges}
                  disabled={saving}
                  className={selectionMode === 'available' ? 'bg-[#5ebc67] hover:bg-[#4da855]' : 'bg-red-500 hover:bg-red-600'}
                  data-testid="save-availability"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilityCalendar;
